import moment from 'moment';
import sha1 from 'crypto-js/sha1';
import * as sessionStorageHelper from '../helpers/SessionStorageHelper';
import * as cookieHelper from '../helpers/CookieHelper';
import Page from 'src/model/page';
import {appLogger} from 'app-lib/helpers/logging';

const logger = appLogger.create('Mixpanel Tracker');

// Event Names
export const pageVisitEvent = 'PAGE VISIT';
export const gotContent = 'GOT CONTENT';
export const shareCopyLinkClicked = 'SHARE MODAL COPY CLICKED';
export const socialShareClicked = 'SOCIAL SHARE CLICKED';
export const signInClicked = 'SIGN IN CLICKED';
export const signOutClicked = 'SIGN OUT CLICKED';
export const signInModalClicked = 'SIGN IN MODAL CLICKED';
export const cardClicked = 'CARD CLICKED';
export const bannerUploadClicked = 'BANNER UPLOAD BUTTON CLICKED';
export const bannerRemoveClicked = 'BANNER REMOVE BUTTON CLICKED';
export const bannerImageUploaded = 'BANNER IMAGE UPLOADED';
export const logoUploadClicked = 'LOGO UPLOAD BUTTON CLICKED';
export const logoRemoveClicked = 'LOGO REMOVE BUTTON CLICKED';
export const logoImageUploaded = 'LOGO IMAGE UPLOADED';
export const themeButtonCLicked = 'THEME BUTTON CLICKED';
export const colorSelected = 'THEME COLOR SELECTED';
export const thumbnailButtonClicked = 'THUMBNAIL IMAGE CLICKED';
export const thumbnailImageUploaded = 'THUMBNAIL IMAGE UPLOADED';
export const categorySelected = 'CATEGORY SELECTED';
export const featuredClicked = 'FEATURED CLICKED';
export const titleChanged = 'TITLE CHANGED';
export const descriptionChanged = 'DESCRIPTION CHANGED';
export const recordingTitleChanged = 'RECORDING TITLE CHANGED';
export const recordingDescriptionChanged = 'RECORDING DESCRIPTION CHANGED';
export const learnMoreClicked = 'LEARN MORE CLICKED';
export const analyticsClicked = 'ANALYTICS CLICKED';
export const registerButtonClicked = 'REGISTER BUTTON CLICKED';
export const registrationModal = 'REGISTER MODAL';
export const akamaiRecording = 'PLAYED AKAMAI RECORDING';
export const bcLoaded = 'BRIGHTCOVE PLAYER LOADED';
export const jwLoaded = 'JWPLAYER LOADED';
export const jwplayerAnalyticsProgress = 'JWPLAYER ANALYTICS PROGRESS';
export const jwplayerAnalyticsSeek = 'JWPLAYER ANALYTICS SEEK';
export const jwplayerAnalyticsFullscreen = 'JWPLAYER ANALYTICS FULLSCREEN';
export const jwplayerAnalyticsComplete = 'JWPLAYER ANALYTICS COMPLETE';
export const jwplayerAnalyticsPause = 'JWPLAYER ANALYTICS PAUSE';
export const bcplayerAnalyticsComplete = 'BRIGHTCOVE ANALYTICS COMPLETE';
export const bcplayerAnalyticsFullscreen = 'BRIGHTCOVE ANALYTICS FULLSCREEN';
export const bcplayerAnalyticsProgress = 'BRIGHTCOVE ANALYTICS PROGRESS';
export const bcplayerAnalyticsSeeked = 'BRIGHTCOVE ANALYTICS SEEKED';
export const bcplayerAnalyticsPause = 'BRIGHTCOVE ANALYTICS PAUSE';
export const privacySelected = 'PRIVACY SELECTED';

// Grouping Events
export const newGroupCreated = 'NEW GROUP CREATED';
export const groupMoveBtnClicked = 'GROUP MOVE BUTTON CLICKED';
export const groupDeleteBtnClicked = 'GROUP DELETE BUTTON CLICKED';
export const groupNameChanged = 'GROUP NAME CHANGED';

// Search Page Prefix
export const searchPage = 'SEARCH';

// Search Events
export const newSearch = 'NEW SEARCH EXECUTED';
export const noSearchResults = 'NO SEARCH RESULTS';
export const searchResultClicked = 'SEARCH RESULT CLICKED';
export const resultCardWatchNow = 'WATCH NOW';
export const resultCardChannel = 'CHANNEL PAGE';
export const loadMore = 'LOAD MORE';

// Terms of Service
export const termsOfServiceFooterAcceptanceClicked = 'TERMS OF SERVICE BANNER ACCEPTED';

// Channel page prefix
export const channelPage = 'CHANNEL';

// watch now page prefix
export const wnPage = 'WN';

// Event attributes keys
export const keyOwnersId = 'ownersId';
export const keyType = 'type';
export const keyPage = 'page';
export const keyAction = 'action';

// Event attributes values
export const valueCicked = 'clicked';
export const valueCopyLink = 'copy Link';
export const valueSocialShare = 'social share';
export const valueViewed = 'viewed';
export const valueCorporate = 'corporate';
export const valueFacebook = 'facebook';
export const valueLinkedin = 'linkedin';
export const valueTwitter = 'twitter';
export const valueGooglePlus = 'google+';
export const valueChannel = 'channel';
export const valueOrganizer = 'organizer';
export const valueAttendee = 'attendee';
export const valueBrandTheme = 'theme';
export const valueBrandBannerImage = 'banner image';
export const valueBrandThumbnailImage = 'thumbnail image';
export const valueThemeSelected = 'selected';
export const valueRemoveBannerImageClick = 'removed';
export const valueBannerImageUploaded = 'uploaded';
export const valueThumbnailImageClick = 'thumbnail image clicked';
export const valueThumbnailImageUploaded = 'thumbnail image uploaded';
export const valueCategory = 'category';
const gotoStageLogoClicked = 'GOTOSTAGE LOGO CLICKED';

// Homepage
const homepagePrefix = 'HP-';
const categoryClicked = `${homepagePrefix}CATEGORY CLICKED`;
const homepageCardClicked = `${homepagePrefix}CONTENT CARD CLICKED`;
const homepageLoadMoreClicked = `${homepagePrefix}LOAD MORE CLICKED`;
const homepageVisit = `${homepagePrefix}${pageVisitEvent}`;

// Modal
export const webinarRedirectModalClosed = 'WEBINAR REDIRECT MODAL CLOSED';

export function setSuperProperties(mixpanelSuperProperties) {
    if (window.mixpanel) {
        window.mixpanel.register(mixpanelSuperProperties);
    }
}

export function track(eventName, attributes) {
    if (window.mixpanel) {
        window.mixpanel.track(eventName, attributes);
    }
}

export function identify(me) {
    if (window.mixpanel) {
        const hashedUserName = sha1(me.userName).toString();
        // if this is the first time identity was created, set an alias, else set the identify
        // check if the the identity was created just now?
        if (me.createdTime) {
            const currentTime = moment().utc();
            const difference = currentTime.diff(me.createdTime, 'minutes');
            if (difference > 2) {
                window.mixpanel.identify(hashedUserName);
            } else {
                window.mixpanel.alias(hashedUserName);
            }
        } else {
            window.mixpanel.identify(hashedUserName);
        }

        const peopleObject = {
            last_login: new Date(),
            socialLoginIn: false
        };
        (hashedUserName) ? peopleObject.email = hashedUserName : null;
        (me.id) ? peopleObject.accountKey = me.id : null;
        (me.displayName) ? peopleObject.displayName = me.displayName : null;
        (me.entitlements) ? peopleObject.socialLoginIn = true : null;

        window.mixpanel.people.set(peopleObject);
    }
}

export function reportUserSessionId() {
    let userSessionId = sessionStorageHelper.getUserSessionId();
    if (!userSessionId) {
        sessionStorageHelper.setUserSessionId();
        userSessionId = sessionStorageHelper.getUserSessionId();
    }
    this.setSuperProperties({userSessionId});
}

export function reportG2sAnonymousId() {
    // const must be named this way to mimic webinar user id const in cookie helper. No way to reference that const as key.
    const g2sAnonymousId = cookieHelper.getG2sAnonymousIdCookie();
    this.setSuperProperties({g2sAnonymousId});
}

export function reportWatchNowPageVisit(contentKey, sourceValue, searchIdValue = null, recommendationPayload = null) {
    const source = (Page.values().includes(sourceValue)) ? sourceValue : Page.EXTERNAL;
    const eventAttributes = {contentKey, source};
    if (searchIdValue && searchIdValue !== '') {
        eventAttributes.search_id = searchIdValue;
    }
    (recommendationPayload) ? eventAttributes.recommendationPayload = recommendationPayload : null;
    this.track(`${wnPage}-${pageVisitEvent}`, eventAttributes);
}

export function reportHomepageVisit(source) {
    const eventAttributes = {source};
    this.track(homepageVisit, eventAttributes);
}

export function reportRecommendationCategoryClick(categoryName) {
    const eventAttributes = {category: categoryName};
    this.track(`${categoryClicked}`, eventAttributes);
}

export function reportRecommendedContentCardClick(type, category, recommendations, recommendationPayload = null) {
    const eventAttributes = {type, category, recommendations};
    (recommendationPayload) ? eventAttributes.recommendationPayload = recommendationPayload : null;
    this.track(`${homepageCardClicked}`, eventAttributes);
}

export function reportLoadMoreClickedOnHomepage(type, page) {
    const eventAttributes = {type, page};
    this.track(`${homepageLoadMoreClicked}`, eventAttributes);
}

export function reportLogoClick(page) {
    const eventAttributes = {page};
    this.track(`${gotoStageLogoClicked}`, eventAttributes);
}

export function setUserIdentity(identity) {
    if (!identity) {
        logger.debug('Cannot identify user without an identity object');
        return;
    }
    this.identify(identity.me);
    // set alias value as a super property
    const hashedUserName = (identity.me.userName) ? sha1(identity.me.userName).toString() : null;
    const userName = (identity.me.userName && identity.me.userName.split('@'));
    const aliasDomain = userName && userName.length > 1 ? userName[1] : null;
    const userId = identity.me.id;
    this.setSuperProperties({aliasValue: hashedUserName, aliasDomain, userId});
}

export function setUserType(userType) {
    if (!userType) {
        logger.debug('Cannot set user type without userType parameter');
        return;
    }
    this.setSuperProperties({user: userType});
}

export function setUserIdentityForRegisteredUser(identity) {
    if (!window.mixpanel) {
        logger.debug('Cannot identify registered user without mixpanel.');
        return;
    }
    if (!identity) {
        logger.debug('Cannot identify registered user without identity object');
        return;
    }
    const hashedEmail = (identity.email) ? sha1(identity.email).toString() : null;
    const splitEmail = (identity.email && identity.email.split('@'));
    const aliasDomain = splitEmail && splitEmail.length > 1 ? splitEmail[1] : null;

    const peopleObject = {
        email: hashedEmail
    };
    (identity.accountKey) ? peopleObject.accountKey = identity.accountKey : null;
    (identity.firstName) ? peopleObject.firstName = identity.firstName : null;
    (identity.lastName) ? peopleObject.lastName = identity.lastName : null;

    window.mixpanel.people.set(peopleObject);
    window.mixpanel.identify(hashedEmail);
    this.setSuperProperties({aliasValue: hashedEmail, aliasDomain});
}

export function setIsChannelPublic(publicValue = null) {
    this.setSuperProperties({isChannelPublic: publicValue});
}




// WEBPACK FOOTER //
// ./src/app-lib/mixpanel/mixpanelTracker.js